/**
 * Barre de navigation : panneau mobile (burger) et sous-menus déroulants, à un
 * ou deux niveaux (Jeux → un jeu → ses modes, et le menu du compte).
 *
 * Un seul mécanisme pour tous les niveaux : le bouton porte `aria-expanded`,
 * son élément parent porte `.is-open`. Le CSS n'a donc qu'un état à styler,
 * que ce soit pour le tiroir déroulant du bureau ou le panneau plein écran
 * empilé (drill-down) du mobile, et l'état affiché est celui que lit un
 * lecteur d'écran.
 *
 * Ouverture au CLIC et non au survol, y compris sur bureau : le premier niveau
 * n'est pas un lien (cliquer « Flag Nerd » déplie ses modes), donc un menu
 * ouvert au survol se refermerait sous le curseur dès qu'on vise un mode en
 * diagonale, et resterait inatteignable au doigt sur les écrans hybrides.
 */
export default class HeaderMenu {
    constructor() {
        this.header = document.querySelector(".site-header");
        if (!this.header) return;

        this.toggleBtn = document.getElementById("menu-toggle");
        this.nav = this.header.querySelector(".main-nav");
        this.controls = this.header.querySelector(".header-controls");
        this.injectBackButtons();
        this.init();
    }

    /**
     * Sous le point de rupture, chaque sous-menu de la nav (Jeux, Entraînement,
     * les modes de chaque jeu) devient un panneau plein écran plutôt qu'un
     * accordéon (voir `.nav-list .submenu` dans `_header.scss`) : il lui faut
     * un moyen de revenir en arrière sans tout refermer. Un seul bouton
     * générique par sous-menu, injecté ici plutôt que répété dans chaque bloc
     * du HTML — `.submenu-account` (hors `.nav-list`) n'en a pas besoin, son
     * popover reste ancré au bouton compte à tout point de rupture.
     */
    injectBackButtons() {
        const label = window.geoNerdApp?.languageManager?.t("menu.back") || "Retour";
        this.header.querySelectorAll(".nav-list .submenu, .nav-list .submenu-modes").forEach((panel) => {
            const back = document.createElement("button");
            back.type = "button";
            back.className = "submenu-back";
            back.textContent = label;
            back.addEventListener("click", (e) => {
                e.stopPropagation();
                this.closeSubmenus(panel.parentElement);
            });
            panel.prepend(back);
        });
    }

    init() {
        this.placeControls();
        this.toggleBtn?.addEventListener("click", (e) => {
            e.stopPropagation();
            this.toggleMobilePanel();
        });

        this.header.querySelectorAll("[aria-expanded]").forEach((btn) => {
            if (btn === this.toggleBtn) return;
            btn.addEventListener("click", (e) => {
                e.stopPropagation();
                this.toggleSubmenu(btn);
            });
        });

        // Un élément de sous-menu suivi referme tout. Les liens rechargent la page,
        // mais pas ceux qui pointent vers la page courante — et « Nouveautés »
        // (une popup) ne navigue pas du tout : sans ça le menu resterait ouvert
        // par-dessus. `.submenu-back` a son propre handler (un seul niveau en
        // arrière, pas tout refermer — voir `injectBackButtons()`).
        this.header.querySelectorAll(".submenu a, .submenu button:not([aria-expanded]):not(.submenu-back)").forEach((el) =>
            el.addEventListener("click", () => this.closeAll())
        );

        document.addEventListener("click", (e) => {
            if (!this.header.contains(e.target)) this.closeAll();
        });
        document.addEventListener("keydown", (e) => {
            if (e.key === "Escape") this.closeAll();
        });
    }

    /**
     * Sous le point de rupture, le bloc compte et le sélecteur de langue
     * descendent dans `.mobile-account-row` (côte à côte, centrés — voir le
     * CSS) : la barre compacte ne peut pas porter le logo, le son, la langue,
     * « S'inscrire / Connexion » ET le burger sans que quelque chose déborde.
     * Le bouton son, lui, reste dans la barre : on coupe le son en pleine
     * partie, pas en ouvrant un menu.
     */
    placeControls() {
        if (!this.nav || !this.controls) return;
        const mobileRow = this.nav.querySelector(".mobile-account-row");
        if (!mobileRow) return;
        // Doit rester aligné sur `$breakpoint` dans _header.scss.
        const query = window.matchMedia("(max-width: 1100px)");
        const place = () => {
            const account = this.header.querySelector(".account-menu");
            const lang = this.header.querySelector(".language-selector");
            const target = query.matches ? mobileRow : this.controls;
            [account, lang].forEach((el) => {
                if (!el || el.parentElement === target) return;
                if (query.matches) target.appendChild(el);
                else target.insertBefore(el, this.toggleBtn);
            });
            // Le compte puis la langue, dans cet ordre — c'est déjà celui posé
            // par le déplacement ci-dessus. Sur bureau seulement, la barre veut
            // son, LANGUE, compte : on inverse les deux derniers.
            if (!query.matches && lang && account) this.controls.insertBefore(lang, account);
        };
        place();
        query.addEventListener("change", place);
    }

    toggleMobilePanel() {
        const open = !document.body.classList.contains("menu-open");
        // Les sous-menus dépliés d'une ouverture précédente ne doivent pas
        // réapparaître tels quels : on repart du panneau replié.
        this.closeSubmenus(this.header);
        document.body.classList.toggle("menu-open", open);
        this.toggleBtn?.setAttribute("aria-expanded", String(open));
    }

    toggleSubmenu(btn) {
        const item = btn.parentElement;
        const isOpen = btn.getAttribute("aria-expanded") === "true";

        // Un seul sous-menu ouvert par niveau : les frères se referment.
        Array.from(item.parentElement.children).forEach((sibling) => {
            if (sibling !== item) this.closeSubmenus(sibling);
        });

        if (isOpen) {
            this.closeSubmenus(item);
            return;
        }
        item.classList.add("is-open");
        btn.setAttribute("aria-expanded", "true");
    }

    /** Referme `root` (s'il est lui-même un item ouvert) et tout ce qu'il contient. */
    closeSubmenus(root) {
        root.classList.remove("is-open");
        root.querySelectorAll(".is-open").forEach((el) => el.classList.remove("is-open"));
        root.querySelectorAll('[aria-expanded="true"]').forEach((btn) => {
            if (btn !== this.toggleBtn) btn.setAttribute("aria-expanded", "false");
        });
    }

    closeAll() {
        this.closeSubmenus(this.header);
        document.body.classList.remove("menu-open");
        this.toggleBtn?.setAttribute("aria-expanded", "false");
    }
}
